/**
 * AFF 3D models — turn one aircraft into one model-layer feature (#820).
 *
 * Every property is a plain number or string: mapbox GeoJSON sources
 * stringify array properties, so vectors are built in the layer's expressions.
 */

import type { AircraftStatus } from './affSymbology';
import { modelHeightAboveTerrain, modelIdFor, modelRotation } from './affModelLayer';

export interface ModelFeatureInput {
  coordinates: [number, number];
  /** Airframe code: RW, BD, TK, CL, FB. */
  code: string;
  status: AircraftStatus;
  heading: number | null | undefined;
  altitudeAslM: number | null | undefined;
  /** From map.getTerrain(). */
  exaggeration: number;
  /** map.queryTerrainElevation at the aircraft: the height is measured from it. */
  groundRenderedM: number;
  /** The model's forward-axis offset. */
  offsetDeg: number;
  /** How far the model hangs below its origin, in model units. */
  belowOriginM: number;
  /** How much smaller this model is drawn than the shared scale (1 = full size). */
  sizeFactor: number;
}

export function toModelFeature(input: ModelFeatureInput): GeoJSON.Feature<GeoJSON.Point> {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: input.coordinates },
    properties: {
      modelId: modelIdFor(input.code),
      elev: modelHeightAboveTerrain(input.altitudeAslM, input.exaggeration, input.groundRenderedM),
      // The model is drawn smaller by its size factor, so the part hanging
      // below its origin shrinks with it.
      belowOrigin: input.belowOriginM * input.sizeFactor,
      size: input.sizeFactor,
      rotDeg: modelRotation(input.heading, input.offsetDeg)[2],
      status: input.status,
    },
  };
}
